import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearch, useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import {
  ReportTable, Caveats, Finding, AdminReportGate, reportGet, formatStatus,
  WAITING_ON_LABEL, WAITING_ON_CLASS,
} from "@/components/reports/report-primitives";
import { CountBarChart } from "@/components/reports/report-charts";
import { ReportDateFilter, last90Days, type Range } from "@/components/reports/report-date-filter";

const openListColumns = [
  { key: "workOrderNumber", header: "Work order" },
  { key: "serviceTypeName", header: "Service", render: (r: any) => r.serviceTypeName ?? "—" },
  { key: "companyName", header: "Client" },
  { key: "status", header: "Status", render: (r: any) => formatStatus(r.status) },
  { key: "waitingOn", header: "Waiting on",
    render: (r: any) => (
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${WAITING_ON_CLASS[r.waitingOn]}`}>
        {WAITING_ON_LABEL[r.waitingOn]}
      </span>
    ) },
  { key: "currentAssignee", header: "Owner",
    render: (r: any) => r.currentAssignee ?? <span className="text-rose-700">Unassigned</span> },
  { key: "ageDays", header: "Open", align: "right" as const, render: (r: any) => `${Math.round(r.ageDays)}d` },
];

export default function OperationsReportPage() {
  usePageTitle("Operations");
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const [reworkRange, setReworkRange] = useState<Range>(last90Days());

  // The URL IS the state for these four, rather than a copy kept in step with it.
  // Mirroring them into useState needs one effect each way, and the state->URL
  // effect reads state that is a render stale whenever the URL changes on its own
  // (back/forward), so the two effects overwrite each other forever. Reading
  // straight from the URL has no second copy to disagree with.
  const params = new URLSearchParams(search);
  const days = Number(params.get("days")) || 14;
  const assignee = params.get("assigneeId") ?? "";
  const waitingFilter = params.get("waitingOn") ?? "";
  const includeLetters = params.get("includeLetters") === "true";

  // Canonical query string, defaults omitted so a clean view keeps a clean URL.
  // Drives the fetch, the CSV link and the URL alike, so they cannot drift apart.
  const stalledParams = new URLSearchParams();
  if (days !== 14) stalledParams.set("days", String(days));
  if (includeLetters) stalledParams.set("includeLetters", "true");
  if (assignee) stalledParams.set("assigneeId", assignee);
  if (waitingFilter) stalledParams.set("waitingOn", waitingFilter);
  const stalledQs = stalledParams.toString();

  // `replace` so clicking through pills does not build up history entries.
  const setFilter = (key: string, value: string | null) => {
    const next = new URLSearchParams(stalledQs);
    if (value) next.set(key, value); else next.delete(key);
    const qs = next.toString();
    setLocation(`${location}${qs ? `?${qs}` : ""}`, { replace: true } as any);
  };

  const stalled = useQuery({
    queryKey: ["/api/reports/stalled-work", days, includeLetters, assignee, waitingFilter],
    // days is omitted at its default; the route defaults to 14 to match.
    queryFn: () => reportGet(`/api/reports/stalled-work?${stalledQs}`),
  });

  const byAge = useQuery({ queryKey: ["/api/reports/open-work-by-age"], queryFn: () => reportGet("/api/reports/open-work-by-age") });
  const unassigned = useQuery({ queryKey: ["/api/reports/unassigned"], queryFn: () => reportGet("/api/reports/unassigned") });
  const aged = useQuery({ queryKey: ["/api/reports/aged-open-work"], queryFn: () => reportGet("/api/reports/aged-open-work?days=120") });
  const rework = useQuery({
    queryKey: ["/api/reports/rework", reworkRange.start, reworkRange.end],
    queryFn: () => reportGet(`/api/reports/rework?start=${reworkRange.start}&end=${reworkRange.end}`),
  });

  const rows: any[] = stalled.data?.rows ?? [];
  // Counts and owner options are computed server-side over the set BEFORE the
  // wait-party filter, so they stay put as you click between pills.
  const coverage = stalled.data?.meta?.coverage;
  const owners: { id: string; name: string }[] = stalled.data?.meta?.facets?.owners ?? [];

  const neverNoted = rows.filter(r => r.daysSinceNote === null).length;
  const DISPLAY_CAP = 50;
  const displayRows = useMemo(() => rows.slice(0, DISPLAY_CAP), [rows]);
  const capped = rows.length > DISPLAY_CAP;

  useEffect(() => {
    const id = window.location.hash.replace(/^#/, "");
    if (!id) return;
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [aged.data, unassigned.data]);

  return (
    <AdminReportGate>
    <ResponsiveLayout title="Operations" subtitle="Current state of the work — not filtered by a period"
      breadcrumbs={[{ label: "Reports", href: "/reports" }, { label: "Operations" }]}
      backTo={{ label: "All reports", href: "/reports" }}
      isLoading={stalled.isLoading}>
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <b>Snapshot report.</b> There is no date filter on open work. An order opened two years ago
          that is still open belongs here — including <b>Work Complete</b> orders that still need
          invoicing or close-out.
        </div>

        {stalled.error && <p className="text-sm text-red-700 mt-4">{(stalled.error as Error).message}</p>}

        <h2 className="text-xl font-medium mt-10 mb-4">Open work by age</h2>
        {byAge.data && (
          <CountBarChart label="Open orders"
            rows={(byAge.data.rows ?? []).map((r: any) => ({ label: r.band, value: r.count }))} />
        )}

        <h2 id="aged" className="text-xl font-medium mt-12 mb-1 scroll-mt-20">Open 120+ days</h2>
        <p className="text-sm text-gray-500 mb-4">
          {(aged.data?.rows ?? []).length} orders still on the board for four months or more
          — including work waiting on FMCSA. Age is not the same as inactivity.
        </p>
        <ReportTable
          columns={openListColumns}
          rows={(aged.data?.rows ?? []).slice(0, 25)}
          onRowClick={(r: any) => { setLocation(`/work-orders/${r.id}`); }}
          emptyMessage="No open order is 120 days old."
        />
        {(aged.data?.rows ?? []).length > 0 && (
          <a href="/api/reports/aged-open-work.csv?days=120" className="text-sm text-indigo-600 hover:underline">
            Download 120+ day orders as CSV
          </a>
        )}

        <h2 id="unassigned" className="text-xl font-medium mt-12 mb-1 scroll-mt-20">Unassigned open work</h2>
        <p className="text-sm text-gray-500 mb-4">
          {(unassigned.data?.rows ?? []).length} orders on the board with no owner.
        </p>
        <ReportTable
          columns={[
            { key: "workOrderNumber", header: "Work order" },
            { key: "serviceTypeName", header: "Service", render: (r: any) => r.serviceTypeName ?? "—" },
            { key: "companyName", header: "Client" },
            { key: "status", header: "Status", render: (r: any) => formatStatus(r.status) },
            { key: "waitingOn", header: "Waiting on",
              render: (r: any) => (
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${WAITING_ON_CLASS[r.waitingOn]}`}>
                  {WAITING_ON_LABEL[r.waitingOn]}
                </span>
              ) },
            { key: "ageDays", header: "Open", align: "right", render: (r: any) => `${Math.round(r.ageDays)}d` },
          ]}
          rows={(unassigned.data?.rows ?? []).slice(0, 25)}
          onRowClick={(r: any) => { setLocation(`/work-orders/${r.id}`); }}
          emptyMessage="Every open order has an owner."
        />
        {(unassigned.data?.rows ?? []).length > 25 && (
          <a href="/api/reports/unassigned.csv" className="text-sm text-indigo-600 hover:underline">Download all unassigned as CSV</a>
        )}

        <h2 className="text-xl font-medium mt-12 mb-1">Work without recent activity</h2>
        <p className="text-sm text-gray-500 mb-4 max-w-3xl">
          “Activity” means a status change or a note, whichever is more recent. Age is a different
          thing — an order opened long ago that moved yesterday is fine. Split by who the current
          status is waiting on, so FMCSA-processing work is not mixed with work sitting on our desk.
          Letter Requests are excluded by default.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <label className="text-sm text-gray-600">No activity in
            <select value={days} onChange={e => setFilter("days", e.target.value === "14" ? null : e.target.value)}
              className="mx-2 border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
              {[7, 14, 21, 30, 45].map(d => <option key={d} value={d}>{d} days</option>)}
            </select>
          </label>
          <select value={assignee} onChange={e => setFilter("assigneeId", e.target.value || null)}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
            <option value="">All owners</option>
            <option value="unassigned">Unassigned</option>
            {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <button onClick={() => setFilter("includeLetters", includeLetters ? null : "true")} aria-pressed={includeLetters}
            className={`text-sm px-3 py-1 rounded-full border ${includeLetters
              ? "border-gray-300 text-gray-600" : "border-indigo-300 bg-indigo-50 text-indigo-800"}`}>
            {includeLetters ? "Including Letter Requests" : "Excluding Letter Requests"}
          </button>
          <span className="text-sm text-gray-500">{rows.length} orders</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Waiting on">
          {([
            ["", "All"],
            ["us", "Waiting on us"],
            ["client", "Waiting on client"],
            ["agency", "Waiting on FMCSA"],
            ["payment", "Waiting on payment"],
          ] as const).map(([v, label]) => (
            <button key={v || "all"} onClick={() => setFilter("waitingOn", v || null)} aria-pressed={waitingFilter === v}
              className={`text-sm px-3 py-1 rounded-full border ${waitingFilter === v
                ? "border-gray-800 bg-gray-800 text-white" : "border-gray-300 text-gray-600"}`}>
              {label}
              {coverage ? ` (${{
                "": coverage.total,
                us: coverage.waitingOnUs,
                client: coverage.waitingOnClient,
                agency: coverage.waitingOnAgency,
                payment: coverage.waitingOnPayment,
              }[v] ?? 0})` : ""}
            </button>
          ))}
        </div>

        {!waitingFilter && !!coverage?.waitingOnUs && (
          <Finding>
            <b>{coverage.waitingOnUs} of these {coverage.total} quiet orders are waiting on us.</b>{" "}
            That is the pile to work first — the rest are waiting on a client, FMCSA, or payment.
          </Finding>
        )}
        {neverNoted > 0 && (
          <Finding>
            <b>{neverNoted} of these {rows.length} orders have never been noted.</b>{" "}
            Compare against the base rate on the Documentation page before drawing a conclusion.
          </Finding>
        )}

        <div className="mt-6">
          <ReportTable
            columns={[
              { key: "workOrderNumber", header: "Work order",
                render: (r: any) => (<><div className="font-medium">{r.workOrderNumber}</div>
                  <div className="text-xs text-gray-500">{r.serviceTypeName ?? r.serviceType}</div></>) },
              { key: "companyName", header: "Client",
                render: (r: any) => <>{r.companyName ?? "—"}{r.companyDeleted && <span className="ml-1 text-xs text-gray-500">(deleted)</span>}</> },
              { key: "status", header: "Status",
                render: (r: any) => (
                  <div>
                    <div>{formatStatus(r.status)}</div>
                    <span className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${WAITING_ON_CLASS[r.waitingOn]}`}>
                      {WAITING_ON_LABEL[r.waitingOn]}
                    </span>
                  </div>
                ) },
              { key: "currentAssignee", header: "Owner",
                render: (r: any) => r.currentAssignee ?? <span className="text-rose-700">Unassigned</span> },
              { key: "ageDays", header: "Open", align: "right", render: (r: any) => `${Math.round(r.ageDays)}d` },
              { key: "daysQuiet", header: "Quiet", align: "right",
                render: (r: any) => <span className={r.daysQuiet >= 30 ? "text-rose-700 font-medium" : undefined}>{`${Math.round(r.daysQuiet)}d`}</span> },
              { key: "daysSinceNote", header: "Last note", align: "right",
                render: (r: any) => r.daysSinceNote === null
                  ? <span className="text-rose-700 font-medium">Never</span>
                  : `${Math.round(r.daysSinceNote)}d` },
            ]}
            rows={displayRows}
            onRowClick={(r: any) => { setLocation(`/work-orders/${r.id}`); }}
            emptyMessage="Nothing is sitting idle at this threshold."
          />
        </div>

        <div className="mt-4 flex items-center gap-4">
          {capped && (
            <span className="text-sm text-gray-500">
              Showing the {DISPLAY_CAP} quietest of {rows.length} orders.
            </span>
          )}
          <a href={`/api/reports/stalled-work.csv?${stalledQs}`}
             className="text-sm text-indigo-600 hover:underline">
            Download all {rows.length} as CSV
          </a>
        </div>

        <Caveats items={stalled.data?.meta?.caveats ?? []} />

        <h2 className="text-xl font-medium mt-12 mb-1">Operational rework</h2>
        <p className="text-sm text-gray-500 mb-4 max-w-3xl">
          Backward moves in the work pipeline during the selected period. The ordinary pay-at-service
          loop through Awaiting Payment is excluded.
        </p>
        <div className="mb-4"><ReportDateFilter value={reworkRange} onChange={setReworkRange} /></div>
        {rework.error && <p className="text-sm text-red-700">{(rework.error as Error).message}</p>}
        <ReportTable
          columns={[
            { key: "workOrderNumber", header: "Work order" },
            { key: "fromStatus", header: "From", render: (r: any) => formatStatus(r.fromStatus) },
            { key: "toStatus", header: "To", render: (r: any) => formatStatus(r.toStatus) },
            { key: "changedAt", header: "When",
              render: (r: any) => new Date(r.changedAt).toLocaleString("en-US", { timeZone: "America/Chicago" }) },
            { key: "automatic", header: "How", render: (r: any) => r.automatic ? "Automatic" : "Staff" },
            { key: "actor", header: "Who moved it", render: (r: any) => r.actor ?? "—" },
            { key: "currentAssignee", header: "Owner now", render: (r: any) => r.currentAssignee ?? "—" },
          ]}
          rows={(rework.data?.rows ?? []).slice(0, 50)}
          onRowClick={(r: any) => { setLocation(`/work-orders/${r.workOrderId}`); }}
          emptyMessage="No backward pipeline moves in this period."
        />
        {(rework.data?.rows ?? []).length > 0 && (
          <a href={`/api/reports/rework.csv?start=${reworkRange.start}&end=${reworkRange.end}`}
             className="text-sm text-indigo-600 hover:underline mt-3 inline-block">Download rework CSV</a>
        )}
        <Caveats items={rework.data?.meta?.caveats ?? []} />
    </ResponsiveLayout>
    </AdminReportGate>
  );
}
